/**
 * Domain × capacity grants — single source for API `@Can` and admin UI `can()`.
 * Packs remain templates that expand into grants (soak until Role.grants lands).
 */

import {
  CAPABILITY_PACK,
  normalizeCapabilityPacks,
  packsForRoles,
  ROLE_PACKS,
  type CapabilityPack,
} from './capability-packs.js';

export const PERMISSION_DOMAIN = {
  CATALOG: 'catalog',
  INVENTORY: 'inventory',
  PROCUREMENT: 'procurement',
  SALES: 'sales',
  CUSTOMERS: 'customers',
  LEADS: 'leads',
  PARTNERS: 'partners',
  SUPPORT: 'support',
  DOCUMENTS: 'documents',
  ACCOUNTING: 'accounting',
  TREASURY: 'treasury',
  PEOPLE: 'people',
  STORAGE: 'storage',
  SETTINGS: 'settings',
} as const;

export type PermissionDomain = (typeof PERMISSION_DOMAIN)[keyof typeof PERMISSION_DOMAIN];

export const ALL_PERMISSION_DOMAINS: readonly PermissionDomain[] = Object.values(PERMISSION_DOMAIN);

/** Capacities allowed per domain (read is always valid). */
export const DOMAIN_CAPACITIES = {
  catalog: ['read', 'create', 'edit', 'price', 'delete'],
  inventory: ['read', 'move', 'delete'],
  procurement: ['read', 'order', 'receive', 'pay', 'delete'],
  sales: ['read', 'create', 'edit', 'commit', 'delete'],
  customers: ['read', 'create', 'edit', 'delete'],
  leads: ['read', 'create', 'edit', 'delete'],
  partners: ['read', 'create', 'edit', 'send', 'delete'],
  support: ['read', 'reply', 'edit', 'delete'],
  documents: ['read', 'generate', 'delete'],
  accounting: ['read', 'post'],
  treasury: ['read', 'post'],
  people: ['read', 'create', 'edit', 'delete'],
  storage: ['read', 'upload', 'delete'],
  settings: ['read', 'edit'],
} as const satisfies Record<PermissionDomain, readonly string[]>;

export type DomainCapacity<D extends PermissionDomain = PermissionDomain> =
  (typeof DOMAIN_CAPACITIES)[D][number];

export type Grant = `${PermissionDomain}:${string}`;

export const GRANT_SOD_FORBIDDEN_PAIRS: ReadonlyArray<readonly [Grant, Grant]> = [
  ['procurement:order', 'procurement:pay'],
  ['sales:commit', 'accounting:post'],
];

const ALL_GRANTS: Grant[] = ALL_PERMISSION_DOMAINS.flatMap((domain) =>
  DOMAIN_CAPACITIES[domain].map((cap) => `${domain}:${cap}` as Grant),
);

export const ALL_DOMAIN_GRANTS: readonly Grant[] = Object.freeze(ALL_GRANTS);

export function isGrant(value: string): value is Grant {
  const [domain, cap] = value.split(':');
  if (!domain || !cap) return false;
  if (!(ALL_PERMISSION_DOMAINS as readonly string[]).includes(domain)) return false;
  const caps = DOMAIN_CAPACITIES[domain as PermissionDomain] as readonly string[];
  return caps.includes(cap);
}

/** Normalize raw grant strings; drop unknowns; expand implied read. */
export function normalizeGrants(raw: readonly string[]): Grant[] {
  const out = new Set<Grant>();
  for (const item of raw) {
    const g = String(item ?? '').trim();
    if (!isGrant(g)) continue;
    out.add(g);
    const [domain, cap] = g.split(':') as [PermissionDomain, string];
    if (cap !== 'read') out.add(`${domain}:read` as Grant);
  }
  return [...out].sort();
}

/**
 * Pack → grants templates.
 * Support is intentionally absent from every pack (SUPER_ADMIN-only by default).
 */
export const PACK_TEMPLATE_GRANTS: Record<CapabilityPack, readonly Grant[]> = {
  [CAPABILITY_PACK.CATALOG_MAINTAIN]: [
    'catalog:read',
    'catalog:create',
    'catalog:edit',
    'catalog:delete',
  ],
  [CAPABILITY_PACK.OFFER_PRICE]: ['catalog:read', 'catalog:price'],
  [CAPABILITY_PACK.STOCK_MOVE]: [
    'inventory:read',
    'inventory:move',
    'inventory:delete',
    'catalog:read',
    'documents:read',
    'documents:generate',
  ],
  [CAPABILITY_PACK.BUY_PROCURE]: [
    'procurement:read',
    'procurement:order',
    'procurement:receive',
    'procurement:delete',
    'partners:read',
    'partners:create',
    'partners:edit',
    'partners:send',
    'inventory:read',
    'catalog:read',
    'documents:read',
    'documents:generate',
  ],
  [CAPABILITY_PACK.BUY_PAY]: ['procurement:read', 'procurement:pay', 'documents:read'],
  [CAPABILITY_PACK.SELL_COMMIT]: [
    'sales:read',
    'sales:create',
    'sales:edit',
    'sales:commit',
    'sales:delete',
    'customers:read',
    'customers:create',
    'customers:edit',
    'customers:delete',
    'leads:read',
    'leads:create',
    'leads:edit',
    'leads:delete',
    'partners:read',
    'partners:create',
    'partners:edit',
    'partners:send',
    'partners:delete',
    'catalog:read',
    'documents:read',
    'treasury:read',
  ],
  [CAPABILITY_PACK.MONEY_READ]: [
    'accounting:read',
    'treasury:read',
    'procurement:read',
    'documents:read',
  ],
  [CAPABILITY_PACK.MONEY_POST]: [
    'accounting:read',
    'accounting:post',
    'treasury:read',
    'treasury:post',
    'documents:read',
  ],
  [CAPABILITY_PACK.PARTY_ADMIN]: [
    'people:read',
    'people:create',
    'people:edit',
    'people:delete',
  ],
  [CAPABILITY_PACK.OPS_VIEW]: [
    'catalog:read',
    'inventory:read',
    'customers:read',
    'documents:read',
    'procurement:read',
    'sales:read',
  ],
  [CAPABILITY_PACK.STORAGE_READ]: ['storage:read'],
  [CAPABILITY_PACK.STORAGE_MANAGE]: ['storage:read', 'storage:upload', 'storage:delete'],
};

/**
 * Expand packs into grants (soak / custom roles still on capabilityPacks).
 * `ops.view` alone = viewer consult set. Beside a job pack it adds nothing
 * (same rule as adminAccess viewerOnly).
 */
export function grantsFromPacks(packs: readonly string[]): Grant[] {
  const normalized = normalizeCapabilityPacks(packs);
  const withoutOps = normalized.filter((p) => p !== CAPABILITY_PACK.OPS_VIEW);
  const effective = withoutOps.length > 0 ? withoutOps : normalized;
  const out = new Set<Grant>();
  for (const pack of effective) {
    for (const g of PACK_TEMPLATE_GRANTS[pack] ?? []) out.add(g);
  }
  return normalizeGrants([...out]);
}

/**
 * Resolve grants for role names.
 * `customGrantsByRole` supplies grants for non-seeded roles.
 * `customPacksByRole` used as fallback when grants empty (soak).
 */
export function grantsForRoles(
  roleNames: readonly string[],
  customGrantsByRole?: Readonly<Record<string, readonly string[]>>,
  customPacksByRole?: Readonly<Record<string, readonly string[]>>,
): Set<Grant> {
  const out = new Set<Grant>();
  for (const name of roleNames) {
    if (name === 'SUPER_ADMIN') {
      for (const g of ALL_DOMAIN_GRANTS) out.add(g);
      continue;
    }
    const customGrants = customGrantsByRole?.[name];
    if (customGrants?.length) {
      for (const g of normalizeGrants(customGrants)) out.add(g);
      continue;
    }
    const packs = packsForRoles([name], customPacksByRole);
    for (const g of grantsFromPacks([...packs])) out.add(g);
  }
  return out;
}

/** Held grants from JWT claim, packs claim, or role lookup. */
export function resolveGrants(input: {
  grants?: readonly string[];
  packs?: readonly string[];
  roleNames: readonly string[];
  customGrantsByRole?: Readonly<Record<string, readonly string[]>>;
  customPacksByRole?: Readonly<Record<string, readonly string[]>>;
}): Set<Grant> {
  if (input.roleNames.includes('SUPER_ADMIN')) return new Set(ALL_DOMAIN_GRANTS);
  if (input.grants?.length) return new Set(normalizeGrants(input.grants));
  if (input.packs?.length) return new Set(grantsFromPacks(input.packs));
  return grantsForRoles(input.roleNames, input.customGrantsByRole, input.customPacksByRole);
}

export function can(
  grants: readonly string[] | undefined,
  roleNames: readonly string[],
  domain: PermissionDomain,
  capacity: string,
  packs?: readonly string[],
): boolean {
  if (roleNames.includes('SUPER_ADMIN')) return true;
  const held = resolveGrants({ grants, packs, roleNames });
  const key = `${domain}:${capacity}` as Grant;
  if (capacity === 'read') {
    for (const g of held) {
      if (g.startsWith(`${domain}:`)) return true;
    }
    return false;
  }
  return held.has(key);
}

export function grantSodViolations(
  grants: ReadonlySet<string> | readonly string[],
  opts?: { isSuperAdmin?: boolean },
): Array<{ a: Grant; b: Grant }> {
  if (opts?.isSuperAdmin) return [];
  const set =
    grants instanceof Set ? grants : new Set(normalizeGrants([...grants]));
  const hits: Array<{ a: Grant; b: Grant }> = [];
  for (const [a, b] of GRANT_SOD_FORBIDDEN_PAIRS) {
    if (set.has(a) && set.has(b)) hits.push({ a, b });
  }
  return hits;
}

export function grantSodBlockingPartner(
  grant: Grant,
  selected: ReadonlySet<string>,
): Grant | null {
  for (const [a, b] of GRANT_SOD_FORBIDDEN_PAIRS) {
    if (grant === a && selected.has(b)) return b;
    if (grant === b && selected.has(a)) return a;
  }
  return null;
}

/** Inbox projection type → permission domain (unmapped → null = always visible). */
export const INBOX_TYPE_DOMAIN: Readonly<Record<string, PermissionDomain>> = {
  ORDER_PENDING: PERMISSION_DOMAIN.SALES,
  B2B_ORDER_DRAFT: PERMISSION_DOMAIN.SALES,
  PO_ORDERED: PERMISSION_DOMAIN.PROCUREMENT,
  SUPPORT_TICKET_OPEN: PERMISSION_DOMAIN.SUPPORT,
  SUPPORT_TICKET_REPLY: PERMISSION_DOMAIN.SUPPORT,
  SUPPORT_TICKET_URGENT: PERMISSION_DOMAIN.SUPPORT,
  BATCH_NEAR_EXPIRY: PERMISSION_DOMAIN.INVENTORY,
  LOT_PRICE_DEVIATION: PERMISSION_DOMAIN.CATALOG,
  IMPORT_JOB_RUNNING: PERMISSION_DOMAIN.CATALOG,
  IMPORT_JOB_FAILED: PERMISSION_DOMAIN.CATALOG,
  IMPORT_JOB_DONE: PERMISSION_DOMAIN.CATALOG,
  CONVENTION_STAMP_REQUESTED: PERMISSION_DOMAIN.PARTNERS,
};

export function inboxTypeReadable(
  type: string,
  grants: readonly string[] | undefined,
  roleNames: readonly string[],
  packs?: readonly string[],
): boolean {
  const domain = INBOX_TYPE_DOMAIN[type];
  if (!domain) return true;
  return can(grants, roleNames, domain, 'read', packs);
}

export const PERMISSION_DOMAIN_LABEL_FR: Record<PermissionDomain, string> = {
  catalog: 'Catalogue',
  inventory: 'Inventaire',
  procurement: 'Achats',
  sales: 'Vente',
  customers: 'Clients',
  leads: 'CRM',
  partners: 'Partenaires',
  support: 'Support',
  documents: 'Documents',
  accounting: 'Comptabilité',
  treasury: 'Trésorerie',
  people: 'Comptes et rôles',
  storage: 'Fichiers',
  settings: 'Réglages',
};

/** Staff-facing resources gated by each domain (admin surfaces). */
export const PERMISSION_DOMAIN_RESOURCE_LABEL_FR: Record<PermissionDomain, string> = {
  catalog: 'Produits',
  inventory: 'Lots et stock',
  procurement: 'Commandes fournisseur',
  sales: 'Commandes et devis',
  customers: 'Clients',
  leads: 'Leads',
  partners: 'Conventions',
  support: 'Tickets',
  documents: 'Documents',
  accounting: 'Grand livre',
  treasury: 'Trésorerie',
  people: 'Utilisateurs et rôles',
  storage: 'Fichiers',
  settings: 'Réglages',
};

/** Full CRM CRUD — pack template + sales:commit backfill. */
export const LEADS_CRUD_GRANTS: readonly Grant[] = [
  'leads:read',
  'leads:create',
  'leads:edit',
  'leads:delete',
];

/**
 * Roles that already held sales:commit (Leads was wired under Vendre) keep Leads
 * after the domain split. Additive; skips when any leads:* already present.
 */
export function backfillLeadsGrantsFromSalesCommit(grants: readonly string[]): Grant[] {
  const normalized = normalizeGrants(grants);
  const hasSalesCommit = normalized.includes('sales:commit');
  const hasAnyLeads = normalized.some((g) => g.startsWith('leads:'));
  if (!hasSalesCommit || hasAnyLeads) return normalized;
  return normalizeGrants([...normalized, ...LEADS_CRUD_GRANTS]);
}

export const CAPACITY_LABEL_FR: Record<string, string> = {
  read: 'Lire',
  create: 'Créer',
  edit: 'Modifier',
  delete: 'Supprimer',
  price: 'Prix',
  move: 'Déplacer',
  order: 'Commander',
  receive: 'Réceptionner',
  pay: 'Payer',
  commit: 'Vendre',
  reply: 'Répondre',
  generate: 'Générer',
  post: 'Comptabiliser',
  upload: 'Déposer',
};

export function grantLabelFr(grant: string): string {
  const [domain, cap] = grant.split(':');
  const d = domain as PermissionDomain;
  const resource = PERMISSION_DOMAIN_RESOURCE_LABEL_FR[d] ?? PERMISSION_DOMAIN_LABEL_FR[d] ?? domain;
  const c = CAPACITY_LABEL_FR[cap ?? ''] ?? cap;
  return `${resource} · ${c}`;
}

export const GRANT_SOD_MESSAGE_FR: Record<string, string> = {
  'procurement:order|procurement:pay':
    'Séparation des tâches : un même rôle ne peut pas à la fois commander chez le fournisseur et payer le fournisseur.',
  'accounting:post|sales:commit':
    'Séparation des tâches : un même rôle ne peut pas à la fois vendre et comptabiliser.',
};

export function grantSodMessagesFr(
  violations: Array<{ a: Grant; b: Grant }>,
): string[] {
  return violations.map((v) => {
    const key = [v.a, v.b].sort().join('|');
    return GRANT_SOD_MESSAGE_FR[key] ?? `${grantLabelFr(v.a)} / ${grantLabelFr(v.b)}`;
  });
}

/** Share of domain×capacity cells held (0–100). */
export function grantCoveragePercent(grants: readonly string[]): number {
  const total = ALL_DOMAIN_GRANTS.length;
  if (total === 0) return 0;
  return Math.round((100 * normalizeGrants(grants).length) / total);
}

export function isStrictlyMoreCapableGrants(
  parentGrants: readonly string[],
  childGrants: readonly string[],
): boolean {
  const parent = new Set(normalizeGrants(parentGrants));
  const child = normalizeGrants(childGrants);
  if (child.length === 0) return parent.size > 0;
  if (!child.every((g) => parent.has(g))) return false;
  return parent.size > child.length;
}

export function compareGrantBreadth(
  aGrants: readonly string[],
  bGrants: readonly string[],
): number {
  const aPct = grantCoveragePercent(aGrants);
  const bPct = grantCoveragePercent(bGrants);
  if (aPct !== bPct) return bPct - aPct;
  return normalizeGrants(bGrants).length - normalizeGrants(aGrants).length;
}

/**
 * Toggle one cell. Write on implies read. Read off clears the domain row.
 */
export function toggleGrant(
  selected: readonly string[],
  grant: Grant,
  on: boolean,
): Grant[] {
  const next = new Set(normalizeGrants(selected));
  const [domain, cap] = grant.split(':') as [PermissionDomain, string];
  if (!domain || !cap) return [...next];
  if (on) {
    next.add(grant);
    if (cap !== 'read') next.add(`${domain}:read` as Grant);
  } else if (cap === 'read') {
    for (const g of [...next]) {
      if (g.startsWith(`${domain}:`)) next.delete(g);
    }
  } else {
    next.delete(grant);
  }
  return normalizeGrants([...next]);
}

export const GRANT_TEMPLATE_ROLE_NAMES = [
  'VIEWER',
  'CATALOG',
  'BUYER',
  'WAREHOUSE',
  'SALES',
  'ACCOUNTANT',
] as const;

export function grantsForSeededTemplate(roleName: string): Grant[] {
  if (roleName === 'SUPER_ADMIN') return [...ALL_DOMAIN_GRANTS];
  return grantsFromPacks([...(ROLE_PACKS[roleName] ?? [])]);
}

export const DEFAULT_CUSTOM_ROLE_GRANTS: readonly Grant[] = grantsFromPacks([
  CAPABILITY_PACK.OPS_VIEW,
]);
